import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  useHistory,
  useLocation,
} from "react-router-dom/cjs/react-router-dom.min";
import loadingImage from "../../assets/loading_trasnparent.gif";
import AgentMoodBoardEdit from "./agentUser/agentMoodBoardEdit";
import AgentTabMoodBoard from "./agentUser/agentTabMoodboard";
import { getSingleAgent, getMoodBoardsSharedWithMe } from "../../AxiosFunctions/Axiosfunctionality";

const NonArtistHomeTabs = ({ activeTab, agentData, setActiveTab }) => {
  const [sharedMoodBoards, setSharedMoodBoards] = useState([]);
  const [selectedMoodBoard, setSelectedMoodBoard] = useState(null);
  const [selectedMoodBoardEditAccess, setSelectedMoodBoardEditAccess] =
    useState(false);
  const [reverseMoodboards, setReverseMoodboards] = useState([]);
  const [loading, setLoading] = useState(false);
  const [haveEditAccess, setHaveEditAccess] = useState(false);
  const [searchedAgentDetails, setSearchedAgentDetails] = useState(null);

  const location = useLocation();
  const history = useHistory();

  const getQueryParam = (param) =>
    new URLSearchParams(location.search).get(param);
  const setQueryParam = (param, value) => {
    const queryParams = new URLSearchParams(location.search);

    if (value === "") {
      queryParams.delete(param);
    } else {
      queryParams.set(param, value);
    }

    history.replace({ search: queryParams.toString() });
  };
  const moodBoardId = getQueryParam("ideaBoardId");
  const agentID = getQueryParam("agentId");
  const preview = getQueryParam("preview");
  const fetchSharedIdeaBoards = async () => {
    try {
      const response = await getMoodBoardsSharedWithMe({
        userId: agentData._id,
      });
      setSharedMoodBoards(response.data.moodBoards);
    } catch (error) {
      console.error("Error fetching shared idea boards", error);
    }
  };
  const handleTab = (tab, moodBoardId = null, sharedByUserId = null) => {
    if (tab === "ideaBoard" || tab === "sharedIdeaBoard") {
      setLoading(true);
    }

    let url = `/?activeTab=${tab}`;

    if (moodBoardId) {
      url += `&ideaBoardId=${moodBoardId}`;
    }

    // Always include agentId in the URL
    if (agentData && agentData._id) {
      url += `&agentId=${agentData._id}`;
    }

    history.push(url);

    setTimeout(() => {
      setLoading(false);
    }, 500);
  };

  const queryTab = getQueryParam("activeTab");
  const ideaBoardId = getQueryParam("ideaBoardId");
  useEffect(() => {
    if (agentData && agentData._id) {
      fetchSharedIdeaBoards();
      const moodboards = [...agentData.moodBoards];
      const rMoodboards = moodboards.reverse();
      setReverseMoodboards(rMoodboards);
    }
  }, [agentData]);

  useEffect(() => {
    if (!agentID && (!agentData || Object.keys(agentData).length === 0)) {
      history.replace("/");
    } else if (agentID) {
      if (agentData) {
        // If in preview mode, no edit access
        setHaveEditAccess(!preview && agentData._id === agentID);
      } else {
        setHaveEditAccess(false);
      }
    } else if (agentData && Object.keys(agentData).length > 0 && !agentID) {
      history.replace(`?agentId=${agentData._id}`);
    }

    // If preview mode, explicitly set editAccess to false
    if (preview) {
      setHaveEditAccess(false);
    }
  }, [agentID, agentData]);

  useEffect(() => {
    const tabsRequiringAgent = [
      "ideaBoard",
      "sharedIdeaBoard",
      "myHome",
      "favoriteArtists",
      "contact",
    ];

    const isValidTab =
      queryTab && [...tabsRequiringAgent, "alphabetical"].includes(queryTab);

    const startTime = Date.now();

    const resolveTabRouting = () => {
      const isLoggedIn = !!agentData?._id;
      const bypassValidation = !!ideaBoardId;

      if (isValidTab) {
        if (
          !tabsRequiringAgent.includes(queryTab) ||
          isLoggedIn ||
          bypassValidation
        ) {
          setActiveTab(queryTab);
          setQueryParam("activeTab", queryTab);
          if (isLoggedIn) setQueryParam("agentId", agentData._id); // ✅ ensure agentId is set
        } else {
          setActiveTab("");
          setQueryParam("activeTab", "");
          setQueryParam("agentId", "");
        }
      } else if (isLoggedIn) {
        setActiveTab("myHome");
        setQueryParam("activeTab", "myHome");
        setQueryParam("agentId", agentData._id); // ✅ include agentId
      } else {
        setActiveTab("");
        setQueryParam("activeTab", "");
        setQueryParam("agentId", "");
      }
    };

    const needsLoginCheck =
      tabsRequiringAgent.includes(queryTab) && !agentData?._id && !ideaBoardId;

    if (needsLoginCheck) {
      const interval = setInterval(() => {
        if (agentData?._id || Date.now() - startTime > 2000) {
          clearInterval(interval);
          resolveTabRouting();
        }
      }, 300);

      return () => clearInterval(interval);
    } else {
      resolveTabRouting();
    }
  }, [agentData]);

  const currentRequest = useRef(null);

  const fetchData = useCallback(async () => {
    if (!agentID) return;
    // Abort the previous request if it's still in progress
    if (currentRequest.current) {
      currentRequest.current.abort();
    }

    const controller = new AbortController();
    currentRequest.current = controller;

    setLoading(true);

    try {
      const responseAgent = await getSingleAgent({
        _id: agentID,
        signal: controller.signal,
      });
      if (responseAgent === "NO AGENT FOUND" || responseAgent === "ERROR") {
        setSearchedAgentDetails(null);
      } else {
        setSearchedAgentDetails(responseAgent);
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error fetching agent details:", error);
        setSearchedAgentDetails(null);
      }
    } finally {
      if (!controller.signal.aborted) {
        setLoading(false);
        currentRequest.current = null;
      }
    }
  }, [agentID]);
  useEffect(() => {
    if (agentID && agentData?._id !== agentID) {
      fetchData();
    } else if (agentData && Object.keys(agentData).length > 0) {
      setSearchedAgentDetails(agentData);
      setLoading(false);
    }
  }, [agentID, agentData, fetchData]);
  useEffect(() => {
    if (
      agentData &&
      moodBoardId &&
      sharedMoodBoards.length > 0 &&
      activeTab === "sharedIdeaBoard"
    ) {
      const moodBoard = sharedMoodBoards.find((mb) => mb._id === moodBoardId);
      setSelectedMoodBoard(moodBoard);
      setSelectedMoodBoardEditAccess(
        moodBoard?.sharedEditEmails &&
          moodBoard.sharedEditEmails.includes(agentData.email)
      );
    }
  }, [moodBoardId, sharedMoodBoards, agentData]);

  if (loading) {
    return (
      <div className="" style={{ width: "full", textAlign: "center" }}>
        <img alt="loading" src={loadingImage} style={{ width: "50px" }} />
      </div>
    );
  }
  return (
    <>
      {activeTab === "ideaBoard" &&
        (moodBoardId ? (
          <AgentMoodBoardEdit
            moodBoardId={moodBoardId}
            searchedAgentDetails={searchedAgentDetails}
            agentDetails={searchedAgentDetails}
            haveEditAccess={haveEditAccess}
            moodBoards={searchedAgentDetails?.moodBoards}
            activeTab={activeTab}
          />
        ) : (
          <div>
            <AgentTabMoodBoard
              handleTab={handleTab}
              activeTab={activeTab}
              searchedAgentDetails={searchedAgentDetails}
              agentDetails={searchedAgentDetails}
              haveEditAccess={haveEditAccess}
              moodBoards={reverseMoodboards}
            />
          </div>
        ))}

      {activeTab === "sharedIdeaBoard" &&
        (moodBoardId && selectedMoodBoard ? (
          <AgentMoodBoardEdit
            moodBoardId={selectedMoodBoard._id}
            searchedAgentDetails={searchedAgentDetails}
            agentDetails={searchedAgentDetails}
            haveEditAccess={selectedMoodBoardEditAccess}
            moodBoards={sharedMoodBoards}
            fetchSharedIdeaBoards={fetchSharedIdeaBoards}
            activeTab={activeTab}
          />
        ) : (
          <div>
            <AgentTabMoodBoard
              handleTab={handleTab}
              activeTab={activeTab}
              searchedAgentDetails={searchedAgentDetails}
              agentDetails={searchedAgentDetails}
              haveEditAccess={haveEditAccess}
              moodBoards={sharedMoodBoards}
            />
          </div>
        ))}
    </>
  );
};

export default NonArtistHomeTabs;
